import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { collection, getDocs } from 'firebase/firestore';
import styles from '../ManagerDashboard/Profile.module.css';
import UserContext from '../../context/UserContext';

const ConnectionsModal = ({ isOpen, onClose, profileData }) => {
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 8;
  
  const navigate = useNavigate();
  const { getProfileData } = useContext(UserContext); // Get from context directly
  
  // Handle user selection - navigate to their profile
  const handleUserSelect = async (user) => {
    onClose();
    navigate(`/${user.userName}`);
  };
  
  // Data fetching function
  const fetchConnections = async (pageNum) => {
    if (!profileData?.userId) return;
    
    setIsLoading(true);
    
    try {
      const db = getFirestoreInstance();
      const connectionsRef = collection(db, 'users', profileData.userId, 'connections');
      const connectionsSnapshot = await getDocs(connectionsRef);
      
      // Collect all connection IDs
      let allConnectionIds = [];
      connectionsSnapshot.docs.forEach(docSnapshot => {
        const data = docSnapshot.data();
        if (data.connectedWith && Array.isArray(data.connectedWith)) {
          allConnectionIds = [...allConnectionIds, ...data.connectedWith];
        }
      });
      
      // Calculate pagination
      const totalCount = allConnectionIds.length;
      const startIndex = 0;
      const endIndex = pageNum * pageSize;
      const currentPageIds = allConnectionIds.slice(startIndex, endIndex);
      
      setHasMore(totalCount > endIndex);
      
      // Fetch user details using getProfileData from UserContext
      const usersData = await Promise.all(
        currentPageIds.map(async (userId) => {
          try {
            const userData = await getProfileData(userId);
            return userData ? { userId, ...userData } : null;
          } catch (error) {
            console.error(`Error fetching user ${userId}:`, error);
            return null;
          }
        })
      );
      
      setConnections(usersData.filter(Boolean));
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && profileData?.userId) {
      fetchConnections(page);
    }
  }, [isOpen, profileData?.userId, page, getProfileData]);
  
  // Reset pagination when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPage(1);
    }
  }, [isOpen]);
  
  const loadMore = () => {
    setPage(prev => prev + 1);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Connections</h2>
        
        {isLoading && page === 1 ? (
          <div className={styles['loading-spinner-container']}>
            <div className={styles['loading-spinner']}></div>
          </div>
        ) : connections.length > 0 ? (
          <>
            <div className={styles['recommenders-list']}>
              {connections.map(user => (
                <div 
                  key={user.userId} 
                  className={styles['recommender-card']}
                  onClick={() => handleUserSelect(user)}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={user.profilePictureUrl || require('../../assets/Images/defaultUserProfilePicture.png')}
                    alt={user.fullName || user.userName}
                    className={styles['recommender-photo']}
                  />
                  <div className={styles['recommender-info']}>
                    <div className={styles['recommender-name-container']}>
                      <h3>{user.fullName || user.userName}</h3>
                      {user.idVerificationStatus === 'Verified' && (
                        <img
                          src={require('../../assets/Images/profileverifiedtick.png')}
                          alt="Verified"
                          className={styles['verified-badge']}
                        />
                      )}
                    </div>
                    <p className={styles.username}>@{user.userName}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {hasMore && (
              <button 
                className={styles['load-more-button']}
                onClick={loadMore}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span>Loading...</span>
                ) : (
                  <span>Show More</span>
                )}
              </button>
            )}
          </>
        ) : (
          <div className={styles['no-data-message']}>
            No connections yet
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionsModal;